import React from 'react';
import PropTypes from 'prop-types';
import {
    WeezHeader,
    FilterSetController,
    Filter,
    Card,
    Status,
    EmptyState,
    Table,
    TableBody,
    TableCell,
    TableRow,
    Icon,
    TableHeaderCell,
    DropdownItem,
    Paginator,
    Dropdown,
    Tooltip
} from '@weezevent/nacre';
import SortDropdown from '../../components/sortDropdown';
import { useTranslation } from 'react-i18next';
import EventModel from '../../models/eventModel';
import DropdownPageSize from '../manager/components/dropdownPageSize';
import RedditionModal from './redditionModal';
import './accounting.scss';
import { useHistory } from 'react-router-dom';
import Style from '../attendee/list/list.module.css';
import { InfoTextToolTipMemo } from '../attendee/list/components/attendee-row/attendee-row';
import LoadingTable from '../../components/loadingTable';
import './taxes.scss';

export default function Accounting({ organization }) {
    const { t } = useTranslation();
    const history = useHistory();

    let [currentPage, setCurrentPage] = React.useState(0);

    let [paginationEvents, setPaginationEvents] = React.useState({
        limit: 50,
        offset: 0
    });

    const [filters, setFilters] = React.useState({ ordering: '-id' });
    const [firstFilter, setFirstFilter] = React.useState(false);
    const [events, setEvents] = React.useState([]);
    const [eventExport, setEventExport] = React.useState(false);
    const [inputSearchValue, setInputSearchValue] = React.useState('');

    let handleSearchQuery = (value, filters) => {
        let query = { ordering: filters.ordering };
        if (value !== '') {
            query.search = value;
        }
        const filterStatus = new Set(filters.status);
        if (filters.status && filters.status.has('future')) {
            filterStatus.delete('future');
            const today = new Date();
            const date = today.getFullYear() + '-' + (today.getMonth() + 1) + '-' + today.getDate();
            query.end_date = date;
        }
        if (filterStatus.size > 0) {
            query.publication_status__in = Array.from(filterStatus).join(',');
        }
        if (filters.date) {
            if (filters.date.after) {
                const objDate = new Date(filters.date.after);
                const date = objDate.getFullYear() + '-' + (objDate.getMonth() + 1) + '-' + objDate.getDate();
                query.start_date = date;
            }
            if (filters.date.before) {
                const objDate = new Date(filters.date.before);
                const date = objDate.getFullYear() + '-' + (objDate.getMonth() + 1) + '-' + objDate.getDate();
                query.end_date = date;
            }
        }

        return query;
    };

    const getURL = React.useCallback(() => {
        let param_object = {};

        param_object = handleSearchQuery(inputSearchValue, filters);
        if (inputSearchValue) {
            param_object['search'] = inputSearchValue;
        }

        param_object['limit'] = paginationEvents.limit;
        param_object['offset'] = paginationEvents.offset;

        return param_object;
    }, [filters, inputSearchValue, paginationEvents]);

    const [, loadingEvents] = EventModel.useApiModel(
        {
            organizationId: organization.id,
            query: getURL(),
            onSuccess: response => setEvents(response)
        },
        [organization, getURL]
    );

    const resetPagination = React.useCallback(() => {
        setPaginationEvents({
            limit: paginationEvents.limit,
            offset: 0
        });
        setCurrentPage(0);
    }, [paginationEvents]);

    const handleSearch = React.useCallback(
        (synth, value) => {
            setInputSearchValue(value);
            resetPagination();
        },
        [resetPagination]
    );

    let handleReset = React.useCallback(() => {
        handleSearch('');
    });

    const updateFilters = React.useCallback(
        (name, value) => {
            setFilters(filters => {
                let previousFilters = { ...filters };
                if (name === 'reset') {
                    resetPagination();
                    return {};
                }
                if (!value) {
                    delete previousFilters[name];
                    resetPagination();
                    return previousFilters;
                }
                let newFilters = {
                    ...previousFilters,
                    [name]: value
                };
                resetPagination();
                return newFilters;
            });
        },
        [resetPagination]
    );

    const label = [
        { label: t('ticket.accounting.filter.eventIdLabel'), name: 'id' },
        { label: t('ticket.accounting.filter.eventLabel'), name: 'name' },
        { label: t('ticket.accounting.filter.startDateLabel'), name: 'start_date' },
        { label: t('ticket.accounting.filter.endDateLabel'), name: 'end_date' }
    ];

    const getColor = str => {
        const colorMap = {
            published: 'green',
            draft: 'orange',
            closed: 'red',
            finished: undefined
        };
        return colorMap[str] || undefined;
    };

    const options = [
        { key: 'draft', text: t('ticket.event.list.statusDraft'), name: 'draft' },
        { key: 'published', text: t('ticket.event.list.statusPublished'), name: 'published' },
        { key: 'closed', text: t('ticket.event.list.statusClosed'), name: 'closed' },
        { key: 'future', text: t('ticket.event.list.statusFutur'), name: 'future' }
    ];

    const countEvents = React.useMemo(() => {
        return Number.parseInt(events?.__meta?.headers.get('total-count')) || 0;
    }, [events]);

    const handlePage = React.useCallback(
        value => {
            setCurrentPage(value);

            setPaginationEvents({
                limit: paginationEvents.limit,
                offset: paginationEvents.limit * value
            });
        },
        [paginationEvents]
    );

    const handlePaginate = React.useCallback(value => {
        setPaginationEvents({
            limit: value,
            offset: 0
        });
    }, []);

    const closeEventById = React.useCallback(eventId => {
        setEvents(prevEvents =>
            prevEvents.map(event => {
                if (event.id === eventId) {
                    event.publication_status = 'closed';
                }
                return event;
            })
        );
    }, []);

    return (
        <>
            {eventExport && <RedditionModal event={eventExport} closeEventById={closeEventById} onClose={() => setEventExport(false)} />}
            <WeezHeader title={t('ticket.accounting.title')}>{t('ticket.accounting.subTitle')}</WeezHeader>
            <Card>
                <Card.Content>
                    <FilterSetController
                        onChange={values => {
                            if (!firstFilter) {
                                setFirstFilter(true);
                                return;
                            }
                            if (Object.keys(values).length === 0) {
                                updateFilters('reset');
                            } else {
                                for (const [key, value] of Object.entries(values)) {
                                    updateFilters(key, value);
                                }
                            }
                        }}
                        search={{
                            onChange: (synth, value) => {
                                handleSearch(synth, value);
                            },
                            handleReset: () => {
                                handleReset('');
                            },
                            placeholder: t('ticket.accounting.filter.searchBarPlaceholder'),
                            value: inputSearchValue
                        }}
                        reset={() => {
                            setFilters({});
                        }}
                        total={countEvents}
                        isLoadingTotal={loadingEvents}
                        labelTotal={countEvents > 1 ? t('ticket.common.events') : t('ticket.common.event')}
                    >
                        <Filter
                            name="status"
                            labelSelectAll={t('ticket.common.action.select-all')}
                            labelName={t('ticket.accounting.filter.statusLabel')}
                            multiple
                            options={options}
                            index="1"
                            labelCTA={t('ticket.common.action.apply')}
                        />
                    </FilterSetController>
                    <div className="dropdown">
                        <DropdownPageSize
                            current={paginationEvents.limit}
                            onChange={value => handlePaginate(value)}
                            onChangeCurrentpage={setCurrentPage}
                            totalCount={countEvents}
                            loading={loadingEvents}
                            inline
                            pageSizes={[10, 20, 50, 100]}
                            labelItem="eventPageSize"
                        />
                    </div>
                </Card.Content>
                <Table useNew className={(Style['list__table'], 'accountingTable')}>
                    <Table.THead>
                        <Table.Tr isHeader>
                            {label.map((obj, key) => (
                                <SortDropdown
                                    key={key}
                                    name={obj.name}
                                    label={obj.label}
                                    ordering={filters.ordering}
                                    items={[
                                        <DropdownItem.Icons
                                            key={1}
                                            item={obj.name === 'name' ? t('ticket.common.ordering.A-Za') : t('ticket.common.ordering.ascending')}
                                            checked={filters.ordering === obj.name}
                                            onClick={() => updateFilters('ordering', obj.name)}
                                        />,
                                        <DropdownItem.Icons
                                            key={2}
                                            item={obj.name === 'name' ? t('ticket.common.ordering.Z-A') : t('ticket.common.ordering.descending')}
                                            checked={filters.ordering === '-' + obj.name}
                                            onClick={() => updateFilters('ordering', '-' + obj.name)}
                                        />
                                    ]}
                                />
                            ))}

                            <TableHeaderCell className={'left'} style={{ width: '100px' }}>
                                {t('ticket.accounting.filter.statusLabel')}
                            </TableHeaderCell>

                            <TableHeaderCell className={'center'} style={{ width: '100px' }}>
                                {t('ticket.accounting.filter.actionLabel')}
                            </TableHeaderCell>
                        </Table.Tr>
                    </Table.THead>
                    {!loadingEvents && events && (
                        <TableBody>
                            {events.map((value, key) => (
                                <TableRow key={key}>
                                    <TableCell>
                                        <InfoTextToolTipMemo title={value.id ? value.id : '-'} />
                                    </TableCell>
                                    <TableCell>
                                        <InfoTextToolTipMemo title={value.name ? value.name : '-'} />
                                    </TableCell>
                                    <TableCell>
                                        <InfoTextToolTipMemo title={value.start_date ? new Date(value.start_date).toLocaleDateString() : '-'} />
                                    </TableCell>
                                    <TableCell>
                                        <InfoTextToolTipMemo title={value.end_date ? new Date(value.end_date).toLocaleDateString() : '-'} />
                                    </TableCell>
                                    <TableCell>
                                        <Status text={t(`eventStatus.${value.publication_status}`)} color={getColor(value.publication_status)} responsive />
                                    </TableCell>
                                    <Table.Cell>
                                        <div className="tableCellContainer">
                                            <div className="tableCell">
                                                <Tooltip title={t('ticket.accounting.tooltips')} triggerOnHover={true} position="top">
                                                    <Icon onClick={() => setEventExport(value)} name="download" />
                                                </Tooltip>
                                            </div>
                                            <div className="contentDropdown">
                                                <Dropdown>
                                                    <Dropdown.Item
                                                        item={<div style={{ padding: '0.5em 0.8em' }}>{t('ticket.accounting.seeTaxes')}</div>}
                                                        onClick={() => history.push(`/ticket/O${organization.id}/E${value.id}/taxes`)}
                                                    />
                                                </Dropdown>
                                            </div>
                                        </div>
                                    </Table.Cell>
                                </TableRow>
                            ))}
                        </TableBody>
                    )}
                </Table>
                <LoadingTable loading={loadingEvents} />
                {!loadingEvents && !events.length && (
                    <EmptyState icon={<Icon name="list" />} title={t('ticket.accounting.noRepayment')} subtitle={t('ticket.accounting.noRepaymentSubtitle')} />
                )}
                <div className="paginator-container">
                    <Paginator currentPage={currentPage} onPageChange={handlePage} totalCount={loadingEvents ? 0 : countEvents} pageSize={paginationEvents.limit} />
                </div>
            </Card>
        </>
    );
}

Accounting.propTypes = {
    organization: PropTypes.object.isRequired
};
